import React from "react"
import classNames from "classnames"

const shimmer = "animate-pulse bg-gray-200"

const TEXT_TITLE_HEIGHTS: Record<string, string> = {
  h1: "h-10 lg:h-14",
  h2: "h-8 lg:h-10",
  h3: "h-7 lg:h-8",
  h4: "h-6 lg:h-7"
}

export const TextSkeleton = ({ capsule }: { capsule?: any }) => {
  const showTitle = capsule
    ? capsule.doNotDisplayTitle !== true && !!capsule.title
    : true
  const headingLevel: string =
    typeof capsule?.headingLevel === "string" &&
    TEXT_TITLE_HEIGHTS[capsule.headingLevel]
      ? capsule.headingLevel
      : "h2"
  const titleHeight = TEXT_TITLE_HEIGHTS[headingLevel]

  const alignment: "left" | "center" | "right" =
    capsule?.alignment === "left" || capsule?.alignment === "right"
      ? capsule.alignment
      : "center"

  const paragraphContent = capsule?.text?.json?.content
  const paragraphCount = Array.isArray(paragraphContent)
    ? paragraphContent.filter((n: any) => n?.nodeType === "paragraph").length
    : 0
  const blocks = Math.max(1, Math.min(paragraphCount || 1, 6))

  const lineAlignClass =
    alignment === "right"
      ? "ml-auto"
      : alignment === "left"
        ? "mr-auto"
        : "mx-auto"

  return (
    <div className="mx-auto max-w-camp-container-mobile px-4 lg:max-w-camp-container">
      {showTitle && (
        <div
          className={classNames(
            `mb-8 ${titleHeight} w-3/4 rounded ${shimmer}`,
            {
              "mx-auto": alignment === "center",
              "ml-auto": alignment === "right",
              "mr-auto": alignment === "left"
            }
          )}
        />
      )}
      <div className="mx-auto my-3 max-w-lg lg:my-6 lg:max-w-3xl">
        {Array.from({ length: blocks }).map((_, blockIdx) => (
          <div
            key={blockIdx}
            className={classNames("mb-4 last:mb-0 space-y-2", {
              "text-center": alignment === "center"
            })}
          >
            <div
              className={`h-4 lg:h-5 w-full rounded ${shimmer} ${lineAlignClass}`}
            />
            <div
              className={`h-4 lg:h-5 w-11/12 rounded ${shimmer} ${lineAlignClass}`}
            />
            <div
              className={`h-4 lg:h-5 w-3/4 rounded ${shimmer} ${lineAlignClass}`}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export const ImageSkeleton = ({ capsule }: { capsule?: any }) => (
  <div className={`aspect-[16/9] w-full ${shimmer}`} />
)

const MultiImageSkeletonFallback = () => (
  <div className="grid grid-cols-2 gap-4 p-4">
    <div className={`aspect-[4/5] ${shimmer} rounded`} />
    <div className={`aspect-[4/5] ${shimmer} rounded`} />
  </div>
)

export const MultiImageSkeleton = ({ capsule }: { capsule?: any }) => {
  const reactId = React.useId()

  if (!capsule) return <MultiImageSkeletonFallback />

  const visibleImagesMobile =
    typeof capsule.visibleImagesMobile === "number" &&
    capsule.visibleImagesMobile > 0
      ? capsule.visibleImagesMobile
      : 1
  const visibleImagesDesktop =
    typeof capsule.visibleImagesDesktop === "number" &&
    capsule.visibleImagesDesktop > 0
      ? capsule.visibleImagesDesktop
      : 1
  const fullWidth = capsule.fullWidth === true
  const cardMaxWidth = capsule.cardMaxWidth
  const spacing = capsule.spacing === true

  const items: any[] = capsule.itemsCollection?.items ?? []
  const firstItem = items[0] ?? null
  const desktopImg = firstItem?.image
  const mobileImg = firstItem?.imageMobile ?? desktopImg

  const desktopW = desktopImg?.width ?? mobileImg?.width
  const desktopH = desktopImg?.height ?? mobileImg?.height
  const mobileW = mobileImg?.width ?? desktopW
  const mobileH = mobileImg?.height ?? desktopH

  const getAspectRatio = (
    width: unknown,
    height: unknown,
    fallback: string
  ): string =>
    typeof width === "number" &&
    Number.isFinite(width) &&
    width > 0 &&
    typeof height === "number" &&
    Number.isFinite(height) &&
    height > 0
      ? `${width} / ${height}`
      : fallback

  const mobileAspect = getAspectRatio(mobileW, mobileH, "4 / 5")
  const desktopAspect = getAspectRatio(desktopW, desktopH, mobileAspect)

  const itemCount =
    items.length > 0
      ? items.length
      : Math.max(visibleImagesMobile, visibleImagesDesktop)

  const needsMobileContainerPadding =
    itemCount === 1 || capsule.multipleLinesMobile === true
  const applyMobileHorizontalPadding =
    needsMobileContainerPadding &&
    !(fullWidth && (capsule.multipleLinesMobile || itemCount === 1))
  const fullWidthSpacingGutter = fullWidth && spacing

  const containerClassNames = classNames({
    "w-full": fullWidth,
    "px-4": fullWidthSpacingGutter,
    "px-4 sm:px-0": applyMobileHorizontalPadding && !fullWidthSpacingGutter,
    "mx-auto": !fullWidth,
    "max-w-camp-container-mobile lg:max-w-camp-container":
      !cardMaxWidth && !fullWidth,
    "lg:max-w-[1014px]": cardMaxWidth === "75%" && !fullWidth,
    "lg:max-w-[676px]": cardMaxWidth === "50%" && !fullWidth,
    "lg:max-w-[338px]": cardMaxWidth === "25%" && !fullWidth
  })

  const safeId = `multiimage-skeleton-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`
  const gap = spacing ? "1.5rem" : "0"

  return (
    <div className={containerClassNames}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            #${safeId} {
              display: grid;
              grid-template-columns: repeat(${visibleImagesMobile}, minmax(0, 1fr));
              gap: ${gap};
            }
            #${safeId} > .multiimage-skeleton-cell { aspect-ratio: ${mobileAspect}; }
            @media (min-width: 640px) {
              #${safeId} {
                grid-template-columns: repeat(${visibleImagesDesktop}, minmax(0, 1fr));
              }
              #${safeId} > .multiimage-skeleton-cell { aspect-ratio: ${desktopAspect}; }
            }
          `
        }}
      />
      <div id={safeId}>
        {Array.from({ length: itemCount }).map((_, i) => (
          <div key={i} className={`multiimage-skeleton-cell ${shimmer}`} />
        ))}
      </div>
    </div>
  )
}

export const HeroSkeleton = ({ capsule }: { capsule?: any }) => {
  // Real CapsuleHero returns null when backgroundImage.url or heading is missing —
  // mirror that so the skeleton doesn't reserve space the real component will collapse.
  if (capsule && (!capsule.backgroundImage?.url || !capsule.heading)) {
    return null
  }

  return (
    <section
      aria-hidden
      className={`relative w-full overflow-hidden min-h-[60vh] ${shimmer}`}
    />
  )
}

export const CollectionSkeleton = ({ capsule }: { capsule?: any }) => (
  <div className="px-4">
    <div className={`h-8 w-48 ${shimmer} rounded mb-4`} />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i}>
          <div className={`aspect-[3/4] ${shimmer} rounded mb-2`} />
          <div className={`h-4 w-full ${shimmer} rounded mb-1`} />
          <div className={`h-4 w-20 ${shimmer} rounded`} />
        </div>
      ))}
    </div>
  </div>
)

export const VideoSkeleton = ({ capsule }: { capsule?: any }) => {
  const previewW = capsule?.previewImage?.width
  const previewH = capsule?.previewImage?.height
  const aspectRatio =
    typeof previewW === "number" &&
    typeof previewH === "number" &&
    previewW > 0 &&
    previewH > 0
      ? `${previewW} / ${previewH}`
      : "16 / 9"
  const fullWidth = capsule?.fullWidth === true

  return (
    <div
      className={classNames(
        "mx-auto",
        fullWidth ? "w-full" : "max-w-camp-container px-4"
      )}
    >
      <div
        className={classNames(
          "relative w-full",
          !fullWidth && "overflow-hidden rounded-xl",
          shimmer
        )}
        style={{ aspectRatio }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-16 w-16 rounded-full bg-gray-300" />
        </div>
      </div>
    </div>
  )
}

export const ActivityCardSkeleton = ({ capsule }: { capsule?: any }) => (
  <div className="px-4 lg:px-6">
    <div className="w-full rounded-2xl max-w-camp-container-mobile lg:max-w-[72rem] mx-auto overflow-hidden bg-white">
      <div className="flex flex-col lg:flex-row">
        <div className="relative w-full lg:w-3/5 lg:self-start">
          <div className={`aspect-[3/2] w-full ${shimmer}`} />
        </div>
        <div className="w-full p-6 lg:w-2/5 lg:p-10 flex flex-col gap-5 justify-between">
          <div className="space-y-5">
            <div className={`h-8 w-3/4 rounded ${shimmer}`} />
            <div className="flex gap-2">
              <div className={`h-6 w-16 rounded ${shimmer}`} />
              <div className={`h-6 w-20 rounded ${shimmer}`} />
            </div>
            <div className="space-y-2">
              <div className={`h-4 w-full rounded ${shimmer}`} />
              <div className={`h-4 w-5/6 rounded ${shimmer}`} />
              <div className={`h-4 w-4/6 rounded ${shimmer}`} />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className={`h-6 w-20 rounded ${shimmer}`} />
            <div className={`h-11 w-32 rounded-lg ${shimmer}`} />
          </div>
        </div>
      </div>
    </div>
  </div>
)

const ActivityCardGroupItemSkeleton = () => (
  <div className="overflow-hidden rounded-2xl bg-white">
    <div className={`aspect-[3/2] w-full ${shimmer}`} />
    <div className="p-6 flex flex-col gap-4">
      <div className={`h-6 w-3/4 rounded ${shimmer}`} />
      <div className="flex gap-2">
        <div className={`h-5 w-14 rounded ${shimmer}`} />
        <div className={`h-5 w-16 rounded ${shimmer}`} />
      </div>
      <div className="space-y-2">
        <div className={`h-3.5 w-full rounded ${shimmer}`} />
        <div className={`h-3.5 w-5/6 rounded ${shimmer}`} />
      </div>
      <div className="flex items-center justify-between gap-4 pt-2">
        <div className={`h-5 w-16 rounded ${shimmer}`} />
        <div className={`h-10 w-28 rounded-lg ${shimmer}`} />
      </div>
    </div>
  </div>
)

export const ActivityCardGroupSkeleton = ({ capsule }: { capsule?: any }) => {
  const reactId = React.useId()
  const safeId = `activity-card-group-skeleton-${reactId.replace(
    /[^a-zA-Z0-9_-]/g,
    ""
  )}`

  const fullWidth = capsule?.fullWidth === true
  const spacing = capsule?.spacing === true
  const visibleCardsMobile =
    typeof capsule?.visibleCardsMobile === "number" &&
    capsule.visibleCardsMobile > 0
      ? capsule.visibleCardsMobile
      : 1
  const visibleCardsDesktop =
    typeof capsule?.visibleCardsDesktop === "number" &&
    capsule.visibleCardsDesktop > 0
      ? capsule.visibleCardsDesktop
      : 1

  const itemsLength = capsule?.activityCardsCollection?.items?.length ?? 0
  const itemCount =
    itemsLength > 0
      ? itemsLength
      : Math.max(visibleCardsMobile, visibleCardsDesktop)

  const containerClassNames = classNames({
    "w-full": fullWidth,
    "mx-auto px-4 max-w-camp-container-mobile lg:max-w-camp-container":
      !fullWidth
  })

  const gap = spacing ? "1.5rem" : "0"

  return (
    <div className={containerClassNames}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            #${safeId} {
              display: grid;
              grid-template-columns: repeat(${visibleCardsMobile}, minmax(0, 1fr));
              gap: ${gap};
            }
            @media (min-width: 640px) {
              #${safeId} {
                grid-template-columns: repeat(${visibleCardsDesktop}, minmax(0, 1fr));
              }
            }
          `
        }}
      />
      <div id={safeId}>
        {Array.from({ length: itemCount }).map((_, i) => (
          <ActivityCardGroupItemSkeleton key={i} />
        ))}
      </div>
    </div>
  )
}

export const UgcCardSkeleton = ({ capsule }: { capsule?: any }) => (
  <div className="mx-auto w-full max-w-camp-container-mobile px-4">
    <div className="w-full max-w-[14.5rem]">
      <div
        className={`relative overflow-hidden rounded-xl bg-[#111111] ${shimmer}`}
        style={{ aspectRatio: "9 / 16" }}
      />
    </div>
  </div>
)

const UgcCardGroupItemSkeleton = () => (
  <div
    className={`relative overflow-hidden rounded-xl bg-[#111111] ${shimmer}`}
    style={{ aspectRatio: "9 / 16" }}
  />
)

export const UgcCardGroupSkeleton = ({ capsule }: { capsule?: any }) => {
  const reactId = React.useId()
  const safeId = `ugc-card-group-skeleton-${reactId.replace(
    /[^a-zA-Z0-9_-]/g,
    ""
  )}`

  const visibleCardsMobile =
    typeof capsule?.visibleCardsMobile === "number" &&
    capsule.visibleCardsMobile > 0
      ? capsule.visibleCardsMobile
      : 2
  const visibleCardsDesktop =
    typeof capsule?.visibleCardsDesktop === "number" &&
    capsule.visibleCardsDesktop > 0
      ? capsule.visibleCardsDesktop
      : 4

  const itemsLength = capsule?.ugcCardsCollection?.items?.length ?? 0
  const itemCount =
    itemsLength > 0
      ? itemsLength
      : Math.max(visibleCardsMobile, visibleCardsDesktop)

  return (
    <div className="mx-auto max-w-camp-container-mobile px-0 lg:max-w-camp-container lg:px-4">
      <style
        dangerouslySetInnerHTML={{
          __html: `
            #${safeId} {
              display: grid;
              grid-template-columns: repeat(${visibleCardsMobile}, minmax(0, 1fr));
              gap: 0.5rem;
            }
            @media (min-width: 640px) {
              #${safeId} {
                grid-template-columns: repeat(${visibleCardsDesktop}, minmax(0, 1fr));
              }
            }
          `
        }}
      />
      <div id={safeId}>
        {Array.from({ length: itemCount }).map((_, i) => (
          <UgcCardGroupItemSkeleton key={i} />
        ))}
      </div>
    </div>
  )
}

export const ActivityTypeSkeleton = ({ capsule }: { capsule?: any }) => (
  <div className="mx-auto max-w-camp-container-mobile px-4 lg:max-w-camp-container">
    <div className="flex flex-col lg:flex-row gap-8">
      <div className="w-full lg:w-[55%]">
        <div className="flex flex-col gap-3">
          <div className={`aspect-[4/3] w-full rounded ${shimmer}`} />
          <div className="flex gap-2">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className={`aspect-square w-16 shrink-0 rounded ${shimmer}`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="w-full lg:w-[45%]">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className={`h-9 w-3/4 rounded ${shimmer}`} />
            <div className={`h-5 w-1/2 rounded ${shimmer}`} />
            <div className="flex gap-2">
              <div className={`h-6 w-16 rounded ${shimmer}`} />
              <div className={`h-6 w-20 rounded ${shimmer}`} />
              <div className={`h-6 w-14 rounded ${shimmer}`} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className={`h-4 w-full rounded ${shimmer}`} />
            <div className={`h-4 w-5/6 rounded ${shimmer}`} />
            <div className={`h-4 w-4/6 rounded ${shimmer}`} />
          </div>
          <div className={`h-14 w-full rounded ${shimmer}`} />
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map(i => (
              <div key={i} className={`h-12 w-full rounded ${shimmer}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
)

export const ButtonSkeleton = ({ capsule }: { capsule?: any }) => {
  if (capsule && !capsule.url) return null

  const caption: string =
    typeof capsule?.caption === "string" ? capsule.caption : ""
  const minWidthCh = Math.max(8, Math.min(caption.length || 8, 48))

  return (
    <div className="mx-auto max-w-camp-container-mobile px-4 lg:max-w-camp-container">
      <div className="flex justify-center">
        <div
          className={`h-12 rounded-md ${shimmer}`}
          style={{ minWidth: `${minWidthCh + 4}ch` }}
        />
      </div>
    </div>
  )
}

export const FormSkeleton = ({ capsule }: { capsule?: any }) => (
  <div className="mx-auto max-w-camp-container px-4">
    <div className="mx-auto max-w-lg text-center lg:max-w-3xl">
      <div
        className={`w-full rounded ${shimmer}`}
        style={{ minHeight: "400px" }}
      />
    </div>
  </div>
)

export const ReferralSkeleton = ({ capsule }: { capsule?: any }) => (
  <div className="mx-auto flex max-w-2xl flex-col items-center px-4">
    <div
      className={`w-full max-w-md rounded-lg ${shimmer}`}
      style={{ minHeight: "220px" }}
    />
  </div>
)

export const EmbedSkeleton = ({ capsule }: { capsule?: any }) => {
  if (capsule && !capsule.url) return null

  return (
    <div className="mx-auto max-w-4xl">
      <div className="aspect-h-9 aspect-w-16">
        <div className={`h-full w-full ${shimmer}`} />
      </div>
    </div>
  )
}

const GiftPickerTriggerSkeleton = () => (
  <div className="relative bg-holiday-green-dark">
    <div className="relative flex flex-col items-center p-12 text-center">
      <div
        className={`md:max-w-[28rem] max-w-[16rem] w-full mb-4 ${shimmer} rounded`}
        style={{ aspectRatio: "16 / 9" }}
      />
      <div
        className={`mb-8 lg:mb-12 h-7 lg:h-8 w-3/4 max-w-md ${shimmer} rounded`}
      />
      <div className="flex items-start space-x-6">
        <div className="flex flex-col items-center space-y-3">
          <div className={`h-12 w-32 rounded-md ${shimmer}`} />
          <div className={`h-4 w-28 rounded ${shimmer}`} />
        </div>
        <div className="flex flex-col items-center space-y-3">
          <div className={`h-12 w-32 rounded-md ${shimmer}`} />
          <div className={`h-4 w-28 rounded ${shimmer}`} />
        </div>
      </div>
    </div>
  </div>
)

const SocialFeedSkeleton = () => (
  <div className="mx-auto max-w-camp-container-mobile px-4 lg:max-w-camp-container">
    <div className={`mb-8 h-8 lg:h-10 w-1/2 mx-auto rounded ${shimmer}`} />
    <div className="flex gap-4 overflow-hidden">
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className={`shrink-0 w-40 sm:w-48 rounded ${shimmer}`}
          style={{ aspectRatio: "9 / 16" }}
        />
      ))}
    </div>
  </div>
)

export const ModuleSkeleton = ({ capsule }: { capsule?: any }) => {
  const moduleType = capsule?.moduleType
  if (moduleType === "gift-picker-trigger") return <GiftPickerTriggerSkeleton />
  if (moduleType === "social-feed") return <SocialFeedSkeleton />
  if (capsule) return null
  return <GiftPickerTriggerSkeleton />
}

export const LocationInfoCardSkeleton = ({ capsule }: { capsule?: any }) => (
  <section className="space-y-4 max-w-camp-container-mobile lg:max-w-[56rem] mx-auto px-4">
    <div className="flex flex-col-reverse overflow-hidden rounded-xl bg-white shadow-sm lg:flex-row">
      <div className="space-y-6 p-6 lg:w-2/5 lg:p-8">
        <div className="space-y-6">
          <div className={`h-7 lg:h-8 w-3/4 rounded ${shimmer}`} />
          <div className="flex gap-4">
            <div className={`h-6 w-6 shrink-0 rounded ${shimmer}`} />
            <div className="flex-1 space-y-2">
              <div className={`h-5 w-full rounded ${shimmer}`} />
              <div className={`h-5 w-5/6 rounded ${shimmer}`} />
              <div className={`h-5 w-2/3 rounded ${shimmer}`} />
              <div className={`mt-2 h-5 w-32 rounded ${shimmer}`} />
            </div>
          </div>
          <div className="flex gap-4">
            <div className={`h-5 w-5 shrink-0 rounded ${shimmer}`} />
            <div className={`h-5 w-2/3 rounded ${shimmer}`} />
          </div>
        </div>
        <div className="space-y-4">
          <div className={`h-7 lg:h-8 w-24 rounded ${shimmer}`} />
          <div className="space-y-2">
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className={`h-4 w-full rounded ${shimmer}`} />
            ))}
          </div>
        </div>
      </div>
      <div className={`h-72 w-full lg:h-auto lg:w-3/5 ${shimmer}`} />
    </div>
  </section>
)

export const AccordionGroupSkeleton = ({ capsule }: { capsule?: any }) => {
  const itemsLength =
    capsule?.accordionsCollection?.items?.filter(Boolean).length ?? 0
  const itemCount = itemsLength > 0 ? itemsLength : 3

  return (
    <div className="mx-auto max-w-camp-container-mobile px-4 lg:max-w-camp-container">
      <div className="mx-auto max-w-lg lg:max-w-3xl">
        <div className="flex flex-col gap-4">
          {Array.from({ length: itemCount }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden bg-white shadow-sm"
            >
              <div className="flex items-center justify-between gap-4 p-6">
                <div className={`h-6 lg:h-7 w-2/3 rounded ${shimmer}`} />
                <div className={`h-5 w-5 shrink-0 rounded ${shimmer}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const MerchingColumnSkeleton = () => (
  <div
    className={`w-full rounded-md ${shimmer}`}
    style={{ aspectRatio: "3 / 4" }}
  />
)

export const MerchingRowSkeleton = ({ capsule }: { capsule?: any }) => {
  const items: any[] = capsule?.columnsCollection?.items ?? []
  const itemCount = items.length > 0 ? items.length : 2
  const fullWidth = itemCount < 2

  return (
    <div
      className={classNames(
        "mx-auto max-w-camp-container-mobile px-4 lg:max-w-camp-container",
        { "grid grid-cols-1 gap-6 lg:grid-cols-2": !fullWidth }
      )}
    >
      {Array.from({ length: itemCount }).map((_, i) => (
        <MerchingColumnSkeleton key={i} />
      ))}
    </div>
  )
}

// Map capsule types to their skeleton components
export const skeletonMap: Record<string, React.FC<{ capsule?: any }>> = {
  CapsuleActivityCard: ActivityCardSkeleton,
  CapsuleText: TextSkeleton,
  CapsuleMultiImage: MultiImageSkeleton,
  CapsuleVideo: VideoSkeleton,
  CapsuleCollection: CollectionSkeleton,
  CapsuleCollectionCarousel: CollectionSkeleton,
  CapsuleCollectionPicks: CollectionSkeleton,
  CapsuleProductList: CollectionSkeleton,
  CapsuleMerchingRow: MerchingRowSkeleton,
  CapsuleButton: ButtonSkeleton,
  CapsuleKlaviyoForm: FormSkeleton,
  CapsuleReferral: ReferralSkeleton,
  CapsuleEmbed: EmbedSkeleton,
  CapsuleHero: HeroSkeleton,
  LocationInfoCard: LocationInfoCardSkeleton,
  CapsuleModule: ModuleSkeleton,
  CapsuleScript: () => null,
  CapsuleActivityType: ActivityTypeSkeleton,
  CapsuleUgcCard: UgcCardSkeleton,
  CapsuleUgcCardGroup: UgcCardGroupSkeleton,
  CapsuleActivityCardGroup: ActivityCardGroupSkeleton,
  CapsuleAccordionGroup: AccordionGroupSkeleton
}

export const getCapsuleSkeleton = (
  capsuleType: string
): React.FC<{ capsule?: any }> => {
  return skeletonMap[capsuleType] || ImageSkeleton
}
